import type {Provider, ResolutionDetails} from '@openfeature/web-sdk';
import {FlagNotFoundError, GeneralError} from '@openfeature/web-sdk';
import {getFeaturesByPlatform} from '@/configs/features';
import {accountFeatures} from '@/configs/features/account';
import {flattenObject} from '@/utils/formatters';

type StaticFeatureProviderOptions = {
  platform?: string;
  accountId?: number;
  devMode?: boolean;
};

export class StaticFeatureProvider implements Provider {
  readonly metadata = {name: 'static-feature-provider'} as const;

  readonly runsOn = 'client';

  private readonly features: Record<string, boolean>;

  private readonly accountId: number;

  constructor(options: StaticFeatureProviderOptions) {
    this.accountId = options.accountId ?? 0;

    const domainFeatures = flattenObject(getFeaturesByPlatform(options.platform));
    const accountOverrides = this.resolveAccountFeatures();
    const localStorageOverrides = this.resolveLocalStorageFeatures();

    this.features = {
      ...domainFeatures,
      ...accountOverrides,
      ...localStorageOverrides,
    };

    if (options.devMode) {
      this.features.errors = true;
    }
  }

  resolveBooleanEvaluation(flagKey: string): ResolutionDetails<boolean> {
    if (!(flagKey in this.features)) {
      throw new FlagNotFoundError(`Flag "${flagKey}" not found in static features`);
    }

    return {value: this.features[flagKey]};
  }

  private resolveAccountFeatures(): Record<string, boolean> {
    const result: Record<string, boolean> = {};

    for (const [feature, ids] of Object.entries(accountFeatures)) {
      result[feature] = ids.includes(this.accountId);
    }

    return result;
  }

  private resolveLocalStorageFeatures(): Record<string, boolean> {
    const result: Record<string, boolean> = {};

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);

      if (!key?.startsWith('features.')) {
        continue;
      }

      const feature = key.replace('features.', '');

      const value = localStorage.getItem(key)?.toLowerCase();

      if (value === 'true') {
        result[feature] = true;
      } else if (value === 'false') {
        result[feature] = false;
      }
    }

    return result;
  }
}
